import { Check } from '@phosphor-icons/react';
import { Button, cn } from '@reelplicate/ui';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';

import { OnboardingActions, OnboardingCard, OnboardingHeader } from './onboarding-card';
import { finishPath } from './steps';

const TASKS = [
  { title: 'Read your brand profile', time: '1 min' },
  {
    title: 'Saving your competitors',
    detail: 'Paste any of their reels into your library to break it down',
    time: '1 min',
  },
  { title: 'Drafting your first 3 reels', time: '2 min' },
];

// Static UI: the checklist advances on a timer and opens "All set" at the end.
const STEP_MS = 2500;

/**
 * Figma Checklist Item (106:53): a 22px marker, the title and a Caption on
 * the right, 12px padding. Done: brand/strong check, muted title, "Done".
 * Current: bg/subtle row, ringed marker with the brand/subtle-hover halo and
 * a one-line detail. Todo: 1.5px border/strong marker and the time it takes.
 */
function ChecklistItem({
  title,
  detail,
  time,
  state,
}: (typeof TASKS)[number] & { state: 'done' | 'current' | 'todo' }) {
  return (
    <li
      aria-current={state === 'current' ? 'step' : undefined}
      className={cn(
        'flex items-start gap-3 rounded-md p-3',
        state === 'current' && 'bg-bg-subtle',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex size-[22px] shrink-0 items-center justify-center rounded-full',
          state === 'done' && 'bg-brand-strong text-icon-inverse',
          state === 'current' &&
            'bg-bg-surface border-brand-strong border-2 shadow-[0_0_0_4px_var(--color-brand-subtle-hover)] motion-safe:animate-pulse',
          state === 'todo' && 'bg-bg-surface border-border-strong border-[1.5px]',
        )}
      >
        {state === 'done' && <Check weight="bold" className="size-3" />}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span
          className={cn(
            'text-body-md-strong',
            state === 'done' ? 'text-text-tertiary' : 'text-text-primary',
          )}
        >
          {title}
          <span className="sr-only">
            {state === 'done' ? ' (done)' : state === 'current' ? ' (in progress)' : ''}
          </span>
        </span>
        {state === 'current' && detail && (
          <span className="text-body-sm text-text-secondary">{detail}</span>
        )}
      </span>
      <span aria-hidden className="text-caption text-text-tertiary shrink-0">
        {state === 'done' ? 'Done' : time}
      </span>
    </li>
  );
}

/**
 * Figma "Onboarding — 8 · Setting up" (128:1457): the progress checklist in
 * a bordered card (radius 16, 8px padding) and "Continue in background".
 */
export function SettingUpPage() {
  const navigate = useNavigate();
  // Opens where Figma shows it: the first task done, the second running.
  const [current, setCurrent] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (current < TASKS.length) setCurrent((c) => c + 1);
      else void navigate(finishPath('done'));
    }, STEP_MS);
    return () => clearTimeout(timer);
  }, [current, navigate]);

  return (
    <OnboardingCard loose onSubmit={(event) => event.preventDefault()}>
      <OnboardingHeader
        loose
        eyebrow="Almost there"
        title="Setting up your workspace"
        subtitle="About a minute. You can leave this page — we’ll email you when your first drafts are ready."
      />
      <ol
        aria-label="Setup progress"
        aria-live="polite"
        className="border-border-subtle rounded-2xl border p-2"
      >
        {TASKS.map((task, i) => (
          <ChecklistItem
            key={task.title}
            {...task}
            state={i < current ? 'done' : i === current ? 'current' : 'todo'}
          />
        ))}
      </ol>
      <OnboardingActions>
        <Button asChild variant="ghost" size="lg">
          <RouterLink to="/">Continue in background</RouterLink>
        </Button>
      </OnboardingActions>
    </OnboardingCard>
  );
}
